// src/features/records/hooks/useRecord.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchRecordsByIds, subscribeToRecord } from '../services/core/queries';
import { subscribeToAttachments } from '../services/attachments/queries';
import useEntityCache from '../stores/entityCache';

const RECORD_NOT_FOUND_CODE = 'not-found';
const ACCESS_REVOKED_CODES = new Set(['permission-denied', RECORD_NOT_FOUND_CODE]);

const getErrorCode = (error) => error?.code || error?.cause?.code || null;

const createRecordNotFoundError = () => {
  const error = new Error('Record not found');
  error.code = RECORD_NOT_FOUND_CODE;
  return error;
};

const normalizeRecordError = (error) => {
  if (!error) return error;

  const code = getErrorCode(error);
  if (code && ACCESS_REVOKED_CODES.has(code)) {
    const normalized = createRecordNotFoundError();
    normalized.code = code || RECORD_NOT_FOUND_CODE;
    return normalized;
  }

  return error;
};

const isAccessRevokedError = (error) => {
  if (!error) return false;
  const code = getErrorCode(error);
  return Boolean(code && ACCESS_REVOKED_CODES.has(code));
};

/**
 * Edge-Indexed hook for fetching individual record data
 *
 * Uses the entity cache for efficient access and optional real-time subscription.
 * Maintains backward compatibility with attachment hydration.
 *
 * @param {string} recordId - The record ID to fetch
 * @param {Object} options - Configuration options
 * @param {string} options.hydrate - Hydration strategy: 'none' | 'onMount' | 'onDemand'
 * @param {boolean} options.realtime - Enable real-time subscription (default false)
 * @returns {Object} Record data with attachments and loading state
 */
export function useRecord(recordId, { hydrate = 'none', realtime = false } = {}) {
  const [record, setRecord] = useState(null);
  const [attachments, setAttachments] = useState([]); // Always an array
  const [attachmentsState, setAttachmentsState] = useState('unloaded'); // 'unloaded' | 'loading' | 'ready' | 'error'
  const [attachmentsError, setAttachmentsError] = useState(null);
  const [recordError, setRecordError] = useState(null);
  const [loading, setLoading] = useState(true);

  const unsubDocRef = useRef(null);
  const unsubAttRef = useRef(null);
  const attachmentsRealtimeStartedRef = useRef(false);
  const docRealtimeStartedRef = useRef(false);

  const getOrFetchMany = useEntityCache((s) => s.getOrFetchMany);

  // Fetch record from entity cache
  useEffect(() => {
    if (!recordId) {
      setLoading(false);
      return;
    }

    // Reset state when recordId changes
    setRecord(null);
    setAttachments([]);
    setAttachmentsState('unloaded');
    setAttachmentsError(null);
    setRecordError(null);
    setLoading(true);
    attachmentsRealtimeStartedRef.current = false;
    docRealtimeStartedRef.current = false;

    // Clean up previous subscriptions
    unsubDocRef.current?.();
    unsubAttRef.current?.();

    let isCurrent = true;

    const startDocRealtime = () => {
      if (!isCurrent || !realtime || docRealtimeStartedRef.current) {
        return;
      }

      docRealtimeStartedRef.current = true;
      unsubDocRef.current = subscribeToRecord(
        recordId,
        (data) => {
          if (!isCurrent) {
            return;
          }

          if (!data) {
            setRecord(null);
            setRecordError((prev) => prev ?? createRecordNotFoundError());
            return;
          }

          setRecord(data);
          setRecordError(null);

          // Update entity cache with latest data
          const upsertMany = useEntityCache.getState().upsertMany;
          upsertMany([data]);
        },
        (err) => {
          if (!isCurrent) {
            return;
          }

          console.error('useRecord: real-time subscription error', err);
          const normalizedError = normalizeRecordError(err);

          if (isAccessRevokedError(err)) {
            setRecord(null);
            setRecordError(normalizedError);
            // Stop the listener once access is revoked to avoid repeated asserts from Firestore
            unsubDocRef.current?.();
            docRealtimeStartedRef.current = false;
          } else {
            setRecordError((prev) => normalizedError || prev || err);
          }
        }
      );
    };

    // Fetch from entity cache
    (async () => {
      try {
        const [entity] = await getOrFetchMany([recordId], fetchRecordsByIds);
        if (entity) {
          if (!isCurrent) {
            return;
          }

          setRecord(entity);
          setRecordError(null);

          startDocRealtime();
        } else {
          if (!isCurrent) {
            return;
          }
          setRecord(null);
          setRecordError(createRecordNotFoundError());
          // Keep realtime subscription for transient misses (e.g., document created shortly after fetch)
          startDocRealtime();
        }
      } catch (err) {
        if (!isCurrent) {
          return;
        }

        const normalizedError = normalizeRecordError(err);
        setRecord(null);
        setRecordError(normalizedError);
        console.error('useRecord: failed to fetch record', err);

        if (!isAccessRevokedError(err)) {
          // Keep realtime subscription for transient misses (e.g., document created shortly after fetch)
          startDocRealtime();
        }
      } finally {
        if (isCurrent) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isCurrent = false;
      unsubDocRef.current?.();
      unsubAttRef.current?.();
      docRealtimeStartedRef.current = false;
    };
  }, [recordId, realtime, getOrFetchMany]);

  // Start attachment real-time subscription
  const startAttachmentsRealtime = useCallback(() => {
    if (
      !recordId ||
      attachmentsRealtimeStartedRef.current ||
      isAccessRevokedError(recordError)
    ) {
      return;
    }

    attachmentsRealtimeStartedRef.current = true;
    setAttachmentsState('loading');
    
    unsubAttRef.current = subscribeToAttachments(
      recordId,
      (atts) => {
        setAttachments(atts || []);
        setAttachmentsState('ready');
        setAttachmentsError(null);
      },
      (err) => {
        setAttachmentsState('error');
        setAttachmentsError(err);
        console.error('useRecord: attachments error', err);
      }
    );
  }, [recordId, recordError]);

  // Apply hydration policy
  useEffect(() => {
    if (hydrate === 'onMount' && recordId && record && !recordError) {
      startAttachmentsRealtime();
    }
  }, [hydrate, recordId, record, recordError, startAttachmentsRealtime]);

  // Manual hydration trigger
  const hydrateAttachments = useCallback(() => {
    if (attachmentsState === 'unloaded') {
      startAttachmentsRealtime();
    }
  }, [attachmentsState, startAttachmentsRealtime]);

  // Dev warning for missing hydration
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const expectedCount = record?.attachmentsSummary?.count || 0;
      if (expectedCount > 0 && attachments.length === 0 && attachmentsState === 'unloaded') {
        const timer = setTimeout(() => {
          if (expectedCount > 0 && attachments.length === 0 && attachmentsState === 'unloaded') {
            console.warn(
              `[useRecord] Record ${recordId} has ${expectedCount} attachments but none are loaded. ` +
              `Did you forget to hydrate? Current state: ${attachmentsState}`
            );
          }
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [record, attachments, attachmentsState, recordId]);

  return {
    record,
    attachments,
    attachmentsState,
    attachmentsError,
    recordError,
    hydrateAttachments,
    loading,
    // Convenience properties
    expectedAttachmentCount: record?.attachmentsSummary?.count || 0,
    attachmentPreviews: record?.attachmentsSummary?.previews || []
  };
}

export default useRecord;
